/**
 * "Push notifications on this device" control.
 *
 * Self-contained card covering every state Web Push can be in, so apps
 * mount it and don't reimplement the permission dance:
 *
 *   mountPushToggle(el, { appSlug: 'intranet' });
 *
 * Web Push is per-origin, so this control governs *this browser on this
 * app* only — the copy says so, because "I already turned it on" is
 * otherwise the obvious wrong assumption when a user meets the second
 * app.
 */
import {
  getPushState, enablePush, disablePush, sendTestPush,
} from '../services/push.js';
import { showToast } from './toast.js';
import { getIcon } from './navigation.js';
import { t } from '../i18n/index.js';

function esc(str) {
  const d = document.createElement('div');
  d.textContent = str == null ? '' : String(str);
  return d.innerHTML;
}

/**
 * Copy for each terminal state. Kept together so the whole matrix is
 * readable in one place rather than scattered through branches.
 */
function stateBody(state, appLabel) {
  if (!state.supported) {
    return { tone: 'muted', text: esc(t('push.state.unsupported')), actions: '' };
  }

  if (state.requiresInstall) {
    return {
      tone: 'info',
      // The single most common support question this will generate.
      text: esc(t('push.state.requires_install', { app: appLabel })),
      actions: '',
    };
  }

  if (!state.configured) {
    return { tone: 'muted', text: esc(t('push.state.not_configured')), actions: '' };
  }

  if (state.permission === 'denied') {
    return { tone: 'warn', text: esc(t('push.state.denied')), actions: '' };
  }

  if (state.subscribed) {
    return {
      tone: 'on',
      text: esc(t('push.state.on')),
      actions:
        `<button class="btn btn-secondary btn-sm" data-push-test>${esc(t('push.send_test'))}</button>`
        + `<button class="btn btn-ghost btn-sm" data-push-off>${esc(t('push.turn_off'))}</button>`,
    };
  }

  return {
    tone: 'off',
    text: esc(t('push.state.off', { app: appLabel })),
    actions: `<button class="btn btn-primary btn-sm" data-push-on>${esc(t('push.enable'))}</button>`,
  };
}

/* Reason code -> message, translated when shown (never at module scope). */
function reasonMessage(reason) {
  switch (reason) {
    case 'denied': return t('push.reason.denied');
    case 'dismissed': return t('push.reason.dismissed');
    case 'requires_install': return t('push.reason.requires_install');
    case 'unsupported': return t('push.reason.unsupported');
    case 'not_configured': return t('push.reason.not_configured');
    case 'no_service_worker': return t('push.reason.no_service_worker');
    default: return t('push.toast.enable_failed');
  }
}

/**
 * Mount the control into `el`.
 *
 * @param {HTMLElement} el
 * @param {{appSlug: string, appLabel?: string}} opts
 */
export async function mountPushToggle(el, opts = {}) {
  const appSlug = opts.appSlug || 'intranet';
  const appLabel = opts.appLabel || t('push.this_app');
  let busy = false;

  async function draw() {
    const state = await getPushState();

    // Nothing useful to offer and nothing to explain — don't render an
    // empty card into the page.
    if (!state.supported && !state.isIos) {
      el.innerHTML = '';
      return;
    }

    const body = stateBody(state, appLabel);
    el.innerHTML = `
      <div class="push-toggle push-toggle-${body.tone}">
        <div class="push-toggle-icon">${getIcon('bell')}</div>
        <div class="push-toggle-main">
          <div class="push-toggle-title">${esc(t('push.title'))}</div>
          <p class="push-toggle-text">${body.text}</p>
          ${body.actions ? `<div class="push-toggle-actions">${body.actions}</div>` : ''}
        </div>
      </div>`;

    const onBtn = el.querySelector('[data-push-on]');
    const offBtn = el.querySelector('[data-push-off]');
    const testBtn = el.querySelector('[data-push-test]');

    if (onBtn) {
      onBtn.addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        onBtn.disabled = true;
        onBtn.textContent = t('push.enabling');
        try {
          const res = await enablePush(appSlug);
          if (res.ok) {
            showToast(t('push.toast.enabled'), 'success');
          } else {
            showToast(reasonMessage(res.reason), 'error');
          }
        } catch (e) {
          showToast(e?.message || t('push.toast.enable_failed'), 'error');
        } finally {
          busy = false;
          await draw();
        }
      });
    }

    if (offBtn) {
      offBtn.addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        offBtn.disabled = true;
        try {
          await disablePush();
          showToast(t('push.toast.disabled'), 'info');
        } catch (e) {
          showToast(e?.message || t('push.toast.disable_failed'), 'error');
        } finally {
          busy = false;
          await draw();
        }
      });
    }

    if (testBtn) {
      testBtn.addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        testBtn.disabled = true;
        testBtn.textContent = t('push.sending');
        try {
          const res = await sendTestPush(appSlug);
          if (res && res.success === false) {
            showToast(res.error || t('push.toast.test_failed'), 'error');
          } else {
            showToast(t('push.toast.test_sent'), 'success');
          }
        } catch (e) {
          // The api client throws ApiError on a 4xx with the server's own
          // `error` string as the message. Swallowing it and showing a
          // generic failure hides exactly the detail that makes a push
          // problem diagnosable ("all subscriptions expired", "VAPID not
          // configured"), so surface it verbatim.
          showToast(e?.message || t('push.toast.test_failed'), 'error');
        } finally {
          busy = false;
          testBtn.disabled = false;
          testBtn.textContent = t('push.send_test');
        }
      });
    }
  }

  await draw();
}
